import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const LOGO_URL = "https://file-service.questprotocol.xyz/file/66708b76-63d2-4318-b223-99757962c06f";

export const generateAssessmentPDF = async (data, assessmentData) => {
  const { personalInfo, ratings, comments } = data;
  
  // Initialize PDF
  const doc = new jsPDF({
    orientation: 'p',
    unit: 'mm',
    format: 'a4'
  });
  
  // Helper for Logo with robust error handling
  const getLogoBase64 = async (url) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3s timeout

      const response = await fetch(url, { 
        mode: 'cors',
        signal: controller.signal 
      });
      clearTimeout(timeoutId);

      if (!response.ok) return null;
      const blob = await response.blob();
      
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.warn("Logo loading skipped:", error.message);
      return null;
    }
  };

  try {
    // 1. Header & Logo
    const logoBase64 = await getLogoBase64(LOGO_URL);
    
    if (logoBase64) {
      try {
        doc.addImage(logoBase64, 'PNG', 165, 12, 25, 25);
      } catch (e) {
        console.warn("Could not add image to PDF:", e);
      }
    }
    
    doc.setFontSize(22);
    doc.setTextColor(46, 16, 101); // brand-primary
    doc.text("Clinical Competency Report", 15, 25);
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text("InnerWorks Counselling - Professional Assessment", 15, 32);
    doc.line(15, 42, 195, 42);

    // 2. Clinician Info Table
    autoTable(doc, {
      startY: 50,
      head: [['Clinician Details', 'Reviewer Details']],
      body: [
        [
          `Name: ${personalInfo.clinicianName || 'N/A'}\nEmail: ${personalInfo.clinicianEmail || 'N/A'}`,
          `Lead: ${personalInfo.teamLeadName || 'N/A'}\nEmail: ${personalInfo.teamLeadEmail || 'N/A'}`
        ],
        [`Date: ${personalInfo.assessmentDate || 'N/A'}`, "Status: Self-Assessment Completed"]
      ],
      theme: 'plain',
      headStyles: { fillColor: [243, 232, 255], textColor: [46, 16, 101], fontStyle: 'bold' },
      styles: { cellPadding: 5, fontSize: 10 }
    });

    // 3. Competency Breakdown
    // Use doc.lastAutoTable.finalY instead of local finalY for better accuracy
    let currentY = doc.lastAutoTable.finalY + 15;

    for (const section of assessmentData) {
      // Check for page break before section title
      if (currentY > 250) { 
        doc.addPage(); 
        currentY = 20; 
      }

      doc.setFontSize(14);
      doc.setTextColor(46, 16, 101);
      doc.text(section.title, 15, currentY);
      currentY += 6;

      const tableRows = section.items.map((item, idx) => {
        const rating = ratings[`${section.id}-${item.id}`] ?? '0';
        const comment = comments[`${section.id}-${item.id}`] || 'No notes provided.';
        return [
          `${section.id}.${idx + 1}\n${item.title}`,
          rating.toString(),
          comment
        ];
      });

      autoTable(doc, {
        startY: currentY,
        head: [['Competency Item', 'Rating', 'Notes & Evidence']],
        body: tableRows,
        theme: 'striped',
        headStyles: { fillColor: [88, 28, 135], textColor: [255, 255, 255] },
        styles: { fontSize: 9, cellPadding: 4, overflow: 'linebreak' },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { cellWidth: 20, halign: 'center', fontStyle: 'bold' },
          2: { cellWidth: 'auto' }
        },
        margin: { top: 20 }
      });

      currentY = doc.lastAutoTable.finalY + 15;
    }

    // 4. Footer & Page Numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i} of ${pageCount} | Generated for ${personalInfo.clinicianName || 'Clinician'}`, 15, 285);
    }

    return doc;
  } catch (error) {
    console.error("Critical PDF Generation Error:", error);
    throw new Error(`PDF Generation failed: ${error.message}`);
  }
};

export const sendAssessmentEmail = async (data) => {
  // Simulate API call
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({ success: true });
    }, 1000);
  });
};